import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "~ui/styled"
import { useLocation } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { CenterFlex } from "~components/El"
import { PopupProvider } from "~components/Popup"
import {
  PC_LIST_PANEL_WIDTH_MAX,
  PC_LIST_PANEL_WIDTH_MIN,
  PC_ME_PANEL_WIDTH,
  getActivePcTab,
  getStoredListPanelWidth,
  isMeTabContext,
  isNoticeCenterRoute,
  isPcTabPath,
  isDetailRoute,
  storeListPanelWidth,
  storeNoticeReturnPath,
  storePcTab,
  type PcTabPath,
} from "~ui/app-shell"
import PcSidebar from "./PcSidebar"
import PcListPanelHeader from "./PcListPanelHeader"
import ChatListScreen from "~pages/app-stack/home-stack/chat-list"
import ContactListScreen from "~pages/app-stack/home-stack/contact-list"
import GroupChatListScreen from "~pages/app-stack/home-stack/group-chat-list"
import MeScreen from "~pages/app-stack/home-stack/me"
import PcDetailWatermark from "./PcDetailWatermark"
import PcDetailOutlet from "./PcDetailOutlet"
import { getPcDetailScopeKey } from "./pc-presentation"
import { NoticeCenterProvider } from "~pages/app-stack/notice-center/notice-center-context"
import NoticeCenterList from "~pages/app-stack/notice-center/notice-center-list"
import NoticeCenterDetail from "~pages/app-stack/notice-center/notice-center-detail"

const Shell = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  background: ${({ theme }) => theme.colors.background};
`

const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: row;
`

const ListPanelWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  min-height: 0;
`

const ListPanel = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
`

const ResizeHandle = styled.div<{ $active?: boolean }>`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  touch-action: none;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: translateX(-50%);
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primary : "transparent"};
    transition: background 0.15s;
  }

  &:hover::after {
    background: ${({ theme }) => theme.colors.border};
  }
`

const ListBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const DetailPanel = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--pc-detail-empty-bg);
  overflow: hidden;
`

const EmptyDetail = styled(CenterFlex)`
  flex: 1;
  flex-direction: column;
  user-select: none;
`

const LIST_SCREENS: Record<Exclude<PcTabPath, "/me">, React.ComponentType> = {
  "/chats": ChatListScreen,
  "/contacts": ContactListScreen,
  "/groups": GroupChatListScreen,
}

function PcLayoutBase() {
  const location = useLocation()
  const pathname = location.pathname
  const [listPanelWidth, setListPanelWidth] = useState(getStoredListPanelWidth)
  const [resizing, setResizing] = useState(false)
  const resizeRef = useRef({ startX: 0, startWidth: listPanelWidth })

  const listPath: PcTabPath = getActivePcTab(pathname)
  const detailScopeKey = getPcDetailScopeKey(listPath, pathname, location.search)
  const meContext = isMeTabContext(pathname)
  const showDetail = isDetailRoute(pathname)
  const noticeCenter = isNoticeCenterRoute(pathname)
  const panelWidth = meContext ? PC_ME_PANEL_WIDTH : listPanelWidth
  const prevPathnameRef = useRef(pathname)

  useEffect(() => {
    const prev = prevPathnameRef.current
    if (pathname === "/notices" && prev !== "/notices") {
      storeNoticeReturnPath(prev)
    }
    prevPathnameRef.current = pathname
  }, [pathname])

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--pc-list-panel-width",
      `${panelWidth}px`,
    )
  }, [panelWidth])

  useEffect(() => {
    if (isPcTabPath(pathname)) {
      storePcTab(pathname)
    }
  }, [pathname])

  const onResizeStart = useCallback(
    (event: React.MouseEvent) => {
      if (meContext) return
      event.preventDefault()
      resizeRef.current = { startX: event.clientX, startWidth: listPanelWidth }
      setResizing(true)
    },
    [listPanelWidth, meContext],
  )

  useEffect(() => {
    if (!resizing) return

    const onMouseMove = (event: MouseEvent) => {
      const { startX, startWidth } = resizeRef.current
      const next = Math.min(
        PC_LIST_PANEL_WIDTH_MAX,
        Math.max(
          PC_LIST_PANEL_WIDTH_MIN,
          Math.round(startWidth + event.clientX - startX),
        ),
      )
      setListPanelWidth(next)
    }

    const onMouseUp = () => {
      setResizing(false)
      setListPanelWidth((width) => {
        storeListPanelWidth(width)
        return width
      })
    }

    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)

    return () => {
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
    }
  }, [resizing])

  const ListScreen =
    listPath !== "/me" ? LIST_SCREENS[listPath as Exclude<PcTabPath, "/me">] : null

  return (
    <Shell>
      <PcSidebar />

      {noticeCenter ? (
        <NoticeCenterProvider>
          <MainArea>
            <ListPanelWrap style={{ width: listPanelWidth }}>
              <ListPanel $width={listPanelWidth}>
                <PcListPanelHeader listPath={listPath} noticeCenter />
                <ListBody>
                  <NoticeCenterList />
                </ListBody>
              </ListPanel>
              <ResizeHandle $active={resizing} onMouseDown={onResizeStart} />
            </ListPanelWrap>

            <DetailPanel>
              <PopupProvider scoped key={`${listPath}|notices|${location.search}`}>
                <NoticeCenterDetail />
              </PopupProvider>
            </DetailPanel>
          </MainArea>
        </NoticeCenterProvider>
      ) : (
        <MainArea>
          <ListPanelWrap style={{ width: panelWidth }}>
            <ListPanel $width={panelWidth}>
              {!meContext ? <PcListPanelHeader listPath={listPath} /> : null}
              <ListBody>
                {meContext ? <MeScreen /> : ListScreen ? <ListScreen /> : null}
              </ListBody>
            </ListPanel>
            {!meContext ? (
              <ResizeHandle $active={resizing} onMouseDown={onResizeStart} />
            ) : null}
          </ListPanelWrap>

          <DetailPanel>
            <PopupProvider scoped key={detailScopeKey}>
              {showDetail ? (
                <PcDetailOutlet />
              ) : (
                <EmptyDetail>
                  <PcDetailWatermark />
                </EmptyDetail>
              )}
            </PopupProvider>
          </DetailPanel>
        </MainArea>
      )}
    </Shell>
  )
}

export default observer(PcLayoutBase)
